// A Ficha Única de uma OS (Fase 6): Coleta, Fechamento e a tabela de peças na
// mesma tela, sem modal. Substitui o antigo par abrirModal/abrirFechamento —
// "fechar" deixa de ser um destino e vira a mesma OS revisitada. As duas
// seções ficam sempre visíveis; Fechamento nasce travado e destrava ao salvar
// a Coleta pela primeira vez. Fechar continua uma ação explícita e separada
// de lançar: só o que tiver `devolvidoEm` gravado entra na Fatura.

import {
  contagensDoLote,
  rotuloSituacao,
  situacaoLote,
  type DadosLote,
  type Lote,
  type SituacaoLote,
} from "../../domain/lotes.ts";
import {
  atrasoEntre,
  descreveAtraso,
  descreveDias,
  paradasDeColeta,
  paradasDoCliente,
  previstoDoLote,
} from "../../domain/paradas.ts";
import { diaSemanaISO, formatData, hojeISO, horaDe, paraISO } from "../../domain/datas.ts";
import { previaDaFicha, tiposComBacklogNaFicha } from "../../domain/devolucao.ts";
import { tiposBaixadosDoLote } from "../../domain/backlog.ts";
import { tiposParaLancamento } from "../../domain/tipos-peca.ts";
import type { Id } from "../../domain/ids.ts";
import type { Snapshot, TipoPeca } from "../../domain/snapshot.ts";
import type { RecebidaComando } from "../../comandos/lotes.ts";
import type { LinhaDevolucaoComando } from "../../comandos/devolucao.ts";
import type { Store } from "../store.ts";
import { clear, el, botao, lerJSONValidado, salvarJSON, situacaoComIcone } from "../ui.ts";
import { icon } from "../icons.ts";
import { campo, campoObrigatorio, grupoCampos, inputData, inputHora } from "./_cadastro.ts";
import { sugestaoSequencia, type SugestaoSequencia } from "../../domain/sequencia-os.ts";
import { comMutacaoOtimista, criarAcaoComando } from "./_acao.ts";
import { criarLinhaTipoPeca, type ContagemColhida, type LinhaTipoPeca } from "./_ficha-tipo-peca.ts";
import { fichaCategorias, type FichaCategorias } from "./_ficha-categorias.ts";
import { resumirCategoria } from "./_resumo-categoria.ts";
import { podeEditarModulo } from "./_permissao.ts";
import { emCorrecao } from "../../domain/fechamento-do-lote.ts";
import type { IntencaoFicha } from "../../domain/ficha-os.ts";
import {
  comandoGravarFicha,
  dadosDoCabecalho,
  memoriaDaFicha,
  NOVA_OS,
  valoresIniciais,
  type CabecalhoDigitado,
  type LinhaDigitada,
} from "./_ficha-digitacao.ts";

export interface CallbacksFicha {
  /** Uma OS nova acabou de ser lançada — o painel deve passar a exibir ela.
   * `lancarOutra` vem true quando o clique foi em "Salvar e lançar outra":
   * a tela deve abrir uma Nova OS em branco (mesmo cliente) em vez de ficar
   * na que acabou de ser salva. */
  aoLancarNova(loteId: Id, opts?: { lancarOutra?: boolean; clienteId?: Id }): void;
  /** Fechar o painel (botão X do cabeçalho) — volta a lista para largura cheia. */
  aoFechar(): void;
}

type SituacaoFicha = SituacaoLote | "novo";
const ROTULO_NOVO = "Novo";

/**
 * Categoria aberta/recolhida à mão, por OS lançada: a ficha remonta do zero a
 * cada mutação do Store, e a escolha da pessoa não pode voltar ao padrão.
 * Vale até recarregar a página. (O que foi DIGITADO e ainda não gravado mora
 * na Ficha em digitação — `memoriaDaFicha`, #91.)
 */
const aberturasPorFicha = new Map<Id, Map<Id, boolean>>();

/** A peça como a linha a colhe, no formato da Ficha em digitação. */
const comoDigitada = (c: ContagemColhida): LinhaDigitada => ({
  recebida: c.recebida,
  retidaInformada: c.retidaInformada,
  devolvida: c.devolvida,
  retidas: c.retidas.map((r) => ({ ...r })),
  conferida: c.conferida,
  conferidaMotivo: c.conferidaMotivo,
});

function ultimaOcorrencia(diasSemana: readonly number[]): string {
  const d = new Date();
  for (let i = 0; i < 7; i++) {
    if (diasSemana.includes(d.getDay())) return paraISO(d);
    d.setDate(d.getDate() - 1);
  }
  return hojeISO();
}

/** A Recebida (e a Retida Informada) de cada linha, pronta pro comando de
 * Lote — usada tanto por "Salvar"/"Lançar coleta" quanto pelo "Fechar OS"
 * que salva a Coleta antes de fechar. */
function colherRecebidas(linhas: ReadonlyMap<Id, LinhaTipoPeca>): RecebidaComando[] {
  return [...linhas.values()].map((l) => {
    const c = l.colher();
    return { tipoId: l.tipoId, quantidade: c.recebida, retidaInformada: c.retidaInformada };
  });
}

/**
 * Lotes com o Fechamento destravado localmente pela pessoa (via "Reabrir
 * fechamento"). Nome deliberadamente diferente da Situação "reaberta" do
 * domínio (ADR-0024, `Lote.reabertoEm`) — são conceitos diferentes, que só
 * coincidem quando a Fatura reaberta é o motivo do destravamento; um Lote
 * simplesmente "fechado" passa por aqui do mesmo jeito. Sem esta marca,
 * salvar a Coleta via "Salvar" (e não "Fechar OS") perderia o destravamento
 * — a ficha remontaria travada de novo, já que no banco o Lote continua
 * fechado/reaberto. Mantém o estado visual até o próximo "Fechar OS".
 */
const lotesComFechamentoDestravado = new Set<Id>();

// Nova OS em sequência da anterior (#88) — conveniência de quem usa, guardada
// no navegador: a escolha "Em Sequência" e a última OS lançada aqui (a
// "OS anterior"). Sem ela, não há sequência.
const CHAVE_SEQUENCIA = "nova-os:em-sequencia";
const CHAVE_ULTIMA_LANCADA = "nova-os:ultima-lancada";
const emSequencia = (): boolean =>
  lerJSONValidado(CHAVE_SEQUENCIA, (v): v is boolean => typeof v === "boolean") === true;
const ultimaLancada = (): Id | null =>
  lerJSONValidado(CHAVE_ULTIMA_LANCADA, (v): v is string => typeof v === "string");

/**
 * Monta a Ficha Única. `lote` é `null` para "Nova OS" (nada gravado ainda).
 * Reconstruída do zero a cada seleção e a cada mudança do Store bem-sucedida
 * — o mesmo padrão de `render()` que o resto da tela já usa.
 *
 * `clientePadrao` pré-seleciona o Cliente numa Nova OS em branco — usado
 * depois de "Salvar e lançar outra", pra não perder o cliente entre uma OS e
 * a próxima do mesmo hotel.
 */
export function montarFichaOS(
  store: Store,
  lote: Lote | null,
  callbacks: CallbacksFicha,
  clientePadrao?: Id,
): HTMLElement {
  const snapAbertura = store.get();
  // Permissão de Módulo (issue #71): sem gravação em "operacional", a ficha
  // inteira fica travada — reaproveita o mesmo `bloqueado` que já existia
  // pra OS fechada/faturada/cancelada, em vez de uma checagem paralela.
  const somenteLeitura = !podeEditarModulo(store, "operacional");
  // o que a Ficha mostra ao montar: memória da página > Rascunho do Fechamento
  // gravado > Fechamento gravado — uma regra só (Ficha em digitação, #91)
  // A Nova OS (ainda sem Lote) também tem memória, na chave NOVA_OS.
  const chaveMemoria = lote?.id ?? NOVA_OS;
  const memoria = memoriaDaFicha.de(snapAbertura, chaveMemoria);
  const iniciais = lote ? valoresIniciais(snapAbertura, lote.id, memoria) : null;
  const ini: CabecalhoDigitado | null = iniciais?.cabecalho ?? memoria?.cabecalho ?? null;
  const situacaoReal: SituacaoFicha = lote ? situacaoLote(snapAbertura, lote.id) : "novo";
  let fechamentoDestravado = lote ? lotesComFechamentoDestravado.has(lote.id) : false;

  const clientesAtivos = [...snapAbertura.clientes]
    .filter((c) => c.ativo || c.id === lote?.clienteId)
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

  // ---------------------------------------------------------- Coleta ----
  const cliente = el("select", {}) as HTMLSelectElement;
  for (const c of clientesAtivos) {
    const o = el("option", { value: c.id }, `${c.codigo} · ${c.nome}`) as HTMLOptionElement;
    if (c.id === (ini?.clienteId ?? clientePadrao ?? clientesAtivos[0]?.id)) o.selected = true;
    cliente.append(o);
  }
  const origem = el("select", {}) as HTMLSelectElement;
  const dataColeta = inputData(ini?.coletaData ?? hojeISO());
  const horaColeta = inputHora();
  horaColeta.value = ini?.coletaHora ?? horaDe(new Date());
  const numeroOs = el("input", {
    type: "number",
    min: "1",
    max: "999999",
    step: "1",
    placeholder: "1044",
  }) as HTMLInputElement;
  if (ini) numeroOs.value = ini.os;
  const sacos = el("input", {
    type: "number",
    min: "0",
    step: "1",
    placeholder: "—",
  }) as HTMLInputElement;
  if (ini) sacos.value = ini.sacos;
  const urgente = el("input", { type: "checkbox" }) as HTMLInputElement;
  urgente.checked = ini?.urgente ?? false;
  const relavagem = el("input", { type: "checkbox" }) as HTMLInputElement;
  relavagem.checked = ini?.relavagem ?? false;
  const temPrazoManual = el("input", { type: "checkbox" }) as HTMLInputElement;
  const prazoData = inputData();
  const prazoHora = inputHora();
  if (ini?.temPrazo) {
    temPrazoManual.checked = true;
    prazoData.value = ini.prazoData;
    prazoHora.value = ini.prazoHora;
  }
  const prazoManualCampos = el(
    "div",
    { class: "prazo-manual-campos" },
    "Previsto para ",
    prazoData,
    " às ",
    prazoHora,
  );
  const sincPrazoManual = (): void => {
    prazoManualCampos.hidden = !temPrazoManual.checked;
  };
  temPrazoManual.addEventListener("change", sincPrazoManual);
  sincPrazoManual();

  const preencherDaParada = (paradaId: string): void => {
    const p = paradasDoCliente(store.get(), cliente.value).find((x) => x.id === paradaId);
    if (!p) return;
    dataColeta.value = ultimaOcorrencia(p.diasSemana);
    horaColeta.value = p.horario;
  };

  const avisoDia = el("p", { class: "aviso aviso-atencao", role: "status" });
  avisoDia.hidden = true;
  const checarDiaParada = (): void => {
    const p = paradasDoCliente(store.get(), cliente.value).find((x) => x.id === origem.value);
    const dia = diaSemanaISO(dataColeta.value);
    const ok = !p || Number.isNaN(dia) || p.diasSemana.includes(dia);
    avisoDia.hidden = ok;
    if (!ok && p) {
      avisoDia.textContent =
        `A data escolhida (${descreveDias([dia])}) não é dia da parada de coleta ` +
        `(${descreveDias(p.diasSemana)} às ${p.horario}).`;
    }
  };

  const sincOrigem = (manter: string | null): void => {
    const paradas = paradasDeColeta(store.get(), cliente.value);
    clear(origem);
    origem.append(el("option", { value: "" }, "Coleta Avulsa"));
    for (const p of paradas) {
      origem.append(
        el("option", { value: p.id }, `${descreveDias(p.diasSemana)} às ${p.horario}`),
      );
    }
    if (manter && paradas.some((p) => p.id === manter)) {
      origem.value = manter;
    } else if (!lote && paradas[0]) {
      origem.value = paradas[0].id;
      preencherDaParada(paradas[0].id);
    } else {
      origem.value = "";
    }
  };
  sincOrigem(ini?.paradaId || null);

  // --- Nova OS em sequência da anterior (#88) ---
  const sugestao = lote ? null : sugestaoSequencia(snapAbertura, ultimaLancada());
  const avisoSequencia = el("div", { class: "aviso aviso-info", role: "status" });
  avisoSequencia.hidden = true;
  const nomeCliente = (id: Id): string => snapAbertura.clientes.find((c) => c.id === id)?.nome ?? "—";
  const quandoSugestao = (s: SugestaoSequencia): string =>
    `${descreveDias([diaSemanaISO(s.data)])} ${formatData(s.data).slice(0, 5)} às ${s.hora}`;
  const aplicarSequencia = (s: SugestaoSequencia): void => {
    cliente.value = s.clienteId;
    sincOrigem(s.paradaId);
    dataColeta.value = s.data;
    horaColeta.value = s.hora;
    numeroOs.value = String(s.os);
    clear(avisoSequencia);
    avisoSequencia.className = s.osOcupada ? "aviso aviso-atencao" : "aviso aviso-info";
    avisoSequencia.append(
      `Em sequência da OS ${s.anteriorOs}: ${nomeCliente(s.clienteId)}, ${quandoSugestao(s)}.`,
      ...(s.osOcupada ? [el("div", {}, `A OS ${s.os} já existe — troque o número antes de lançar.`)] : []),
    );
    avisoSequencia.hidden = false;
  };
  const aplicarEmBranco = (): void => {
    cliente.value = clientePadrao ?? clientesAtivos[0]?.id ?? "";
    dataColeta.value = hojeISO();
    horaColeta.value = horaDe(new Date());
    numeroOs.value = "";
    sincOrigem(null);
    avisoSequencia.hidden = true;
  };
  // editou à mão o que a sequência preencheu: o aviso "Em sequência da OS…"
  // (e o de número em uso) deixa de valer
  for (const campoSugerido of [cliente, origem, dataColeta, horaColeta]) {
    campoSugerido.addEventListener("change", () => (avisoSequencia.hidden = true));
  }
  numeroOs.addEventListener("input", () => (avisoSequencia.hidden = true));
  let escolhaSequencia: HTMLElement | null = null;
  if (!lote) {
    const nome = "nova-os-inicio";
    const radioBranco = el("input", { type: "radio", name: nome }) as HTMLInputElement;
    const radioSeq = el("input", { type: "radio", name: nome }) as HTMLInputElement;
    radioSeq.disabled = !sugestao || somenteLeitura;
    radioBranco.disabled = somenteLeitura;
    const ligada = sugestao != null && emSequencia();
    radioSeq.checked = ligada;
    radioBranco.checked = !ligada;
    const escolher = (seq: boolean): void => {
      salvarJSON(CHAVE_SEQUENCIA, seq);
      if (seq && sugestao) aplicarSequencia(sugestao);
      else aplicarEmBranco();
      montarTabela();
      checarDiaParada();
    };
    radioBranco.addEventListener("change", () => escolher(false));
    radioSeq.addEventListener("change", () => escolher(true));
    escolhaSequencia = grupoCampos(
      "Começar a Nova OS",
      {},
      el("label", { class: "check" }, radioBranco, " Em branco"),
      el(
        "label",
        { class: "check" },
        radioSeq,
        sugestao
          ? ` Em sequência da OS ${sugestao.anteriorOs} (OS ${sugestao.os} · ${nomeCliente(sugestao.clienteId)} · ${quandoSugestao(sugestao)})`
          : " Em sequência (disponível depois de lançar uma OS)",
      ),
    );
    // o que já foi digitado nesta Nova OS vence a sugestão em sequência
    if (ligada && sugestao && !ini) aplicarSequencia(sugestao);
    if (ini) {
      // `sincOrigem` sugere a 1ª Parada numa Nova OS: volta ao digitado
      origem.value = ini.paradaId;
      dataColeta.value = ini.coletaData;
      horaColeta.value = ini.coletaHora;
    }
  }
  checarDiaParada();
  origem.addEventListener("change", () => {
    preencherDaParada(origem.value);
    checarDiaParada();
  });
  dataColeta.addEventListener("change", checarDiaParada);

  /** O cabeçalho como está nos campos (texto cru — ver `CabecalhoDigitado`). */
  const colherCabecalho = (): CabecalhoDigitado => ({
    clienteId: cliente.value,
    paradaId: origem.value,
    coletaData: dataColeta.value,
    coletaHora: horaColeta.value,
    os: numeroOs.value,
    sacos: sacos.value,
    urgente: urgente.checked,
    relavagem: relavagem.checked,
    temPrazo: temPrazoManual.checked,
    prazoData: prazoData.value,
    prazoHora: prazoHora.value,
  });
  const colherDadosLote = (): DadosLote => dadosDoCabecalho(colherCabecalho());

  // ------------------------------------------------------- Fechamento ----
  const dataDevolucao = inputData();
  const horaDevolucao = inputHora();
  const previsto = lote ? previstoDoLote(snapAbertura, lote) : null;
  // digitada > fechamento gravado (ou o guardado, reaberta para correção) >
  // rascunho salvo > previsto — a regra da Ficha em digitação
  dataDevolucao.value = iniciais?.devolucao?.data ?? (previsto ? paraISO(previsto) : hojeISO());
  horaDevolucao.value = iniciais?.devolucao ? iniciais.devolucao.hora : previsto ? horaDe(previsto) : "";

  const veredito = el("p", { role: "status" });
  const recalcVeredito = (): void => {
    clear(veredito);
    if (!previsto || !dataDevolucao.value || !horaDevolucao.value) {
      veredito.hidden = true;
      return;
    }
    veredito.hidden = false;
    const [y, mo, d] = dataDevolucao.value.split("-").map(Number);
    const [h, mi] = horaDevolucao.value.split(":").map(Number);
    const dev = new Date(y!, mo! - 1, d!, h!, mi!);
    const r = atrasoEntre(previsto, dev);
    veredito.className = r.atrasado ? "aviso aviso-erro" : "aviso aviso-sucesso";
    veredito.textContent = r.atrasado
      ? `Entregue com atraso — ${descreveAtraso(r.minutos)} depois do previsto (${horaDe(previsto)}).`
      : `No prazo (previsto para ${horaDe(previsto)}).`;
  };
  dataDevolucao.addEventListener("input", recalcVeredito);
  horaDevolucao.addEventListener("input", recalcVeredito);
  recalcVeredito();

  // --------------------------------------------------- tabela de peças ----
  const linhas = new Map<Id, LinhaTipoPeca>();
  const areaTabela = el("div", {});
  const totais = el("p", { class: "contagem-total" });
  let categorias: FichaCategorias | null = null;

  /** Cada edição vai para a Ficha em digitação na hora — QUALQUER mutação do
   * Store remonta a ficha do zero, inclusive uma que nada tem a ver com esta
   * OS (ex.: o "Gerenciar" de Motivos dentro da própria linha). */
  const lembrarCabecalho = (): void => {
    memoriaDaFicha.lembrarCabecalho(store.get(), chaveMemoria, colherCabecalho());
  };
  const lembrarDevolucao = (): void => {
    if (lote) memoriaDaFicha.lembrarDevolucao(store.get(), lote.id, { data: dataDevolucao.value, hora: horaDevolucao.value });
  };
  /** As peças como estão na tela, para o comando "gravar a Ficha". */
  const colherLinhas = (): Map<Id, LinhaDigitada> =>
    new Map([...linhas.values()].map((l) => [l.tipoId, comoDigitada(l.colher())]));

  const tiposDaFicha = (snap: Snapshot): TipoPeca[] => {
    const visiveis = tiposParaLancamento(snap, cliente.value);
    const ids = new Set(visiveis.map((t) => t.id));
    if (lote) {
      for (const c of contagensDoLote(snap, lote.id)) {
        if (c.recebida > 0 || c.devolvida > 0) ids.add(c.tipoId);
      }
      for (const r of snap.retencoes) if (r.loteId === lote.id) ids.add(r.tipoId);
      // peça que só voltou de carona do Backlog (sem Recebida neste Lote)
      for (const tipoId of tiposBaixadosDoLote(snap, lote.id)) ids.add(tipoId);
      const devolucao = { data: dataDevolucao.value, hora: horaDevolucao.value || null };
      for (const id of tiposComBacklogNaFicha(snap, lote.id, cliente.value, devolucao)) ids.add(id);
    }
    return [...snap.tiposPeca]
      .filter((t) => ids.has(t.id))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  };

  const recalcTotais = (): void => {
    // os números de cada peça são os da Prévia do Fechamento (#91): a mesma
    // conta do "Fechar OS", e a composição abaixo sempre fecha
    const itens = [...linhas.values()].map((l) => l.resumo());
    const { devolvida: dev, retida: ret, conferida: conf, semDestino } = resumirCategoria(itens);
    const back = [...linhas.values()].reduce((a, l) => a + l.excedenteAtual(), 0);
    const divConf = itens.reduce((a, i) => a + Math.abs(i.recebida - i.rende), 0);
    categorias?.atualizar();
    clear(totais);
    // Composição das retidas (ADR-0023): Backlog + Conferida − Devolvidas.
    const composicao =
      ret > 0
        ? el(
            "span",
            { class: "ficha-composicao-retidas" },
            `Retidas: ${back} do Backlog + ${conf} conferidas − ${dev} devolvidas` +
              (semDestino > 0 ? ` − ${semDestino} sem destino` : "") +
              ` = ${ret}`,
          )
        : null;
    totais.append(
      "Devolvidas ",
      el("b", { class: "num" }, String(dev)),
      " · retidas ",
      el("b", { class: "num" }, String(ret)),
      " · do Backlog ",
      el("b", { class: "num" }, String(back)),
      " · divergência conferida ",
      el("b", { class: divConf > 0 ? "num num-divergencia" : "num" }, String(divConf)),
      ...(composicao ? [composicao] : []),
    );
  };

  // Recusas do "Fechar OS" que não são de uma peça (data/hora, OS posterior
  // que não pode ser refeita): aparecem antes do clique, perto dos botões.
  const avisoGeral = el("div", { class: "ficha-aviso-geral" });

  /**
   * Prévia do Fechamento da OS inteira (#84) — a mesma conta do "Fechar OS",
   * com a Coleta digitada — entregue a cada linha (a parte dela) e ao aviso
   * geral. A Ficha não calcula nada do Fechamento por conta própria.
   */
  /** Enquanto `montarTabela` cria as linhas, a Prévia espera a última (uma conta só). */
  let montandoLinhas = false;
  const atualizarPrevia = (): void => {
    if (montandoLinhas) return;
    clear(avisoGeral);
    if (!lote) {
      for (const l of linhas.values()) l.aplicarPrevia(null, []);
      return;
    }
    const coleta = [...linhas.values()].map((l) => {
      const c = l.colher();
      return { tipoId: c.tipoId, recebida: c.recebida, retidaInformada: c.retidaInformada };
    });
    const previa = previaDaFicha(store.get(), lote.id, coleta, colherFechamento(), hojeISO());
    for (const [tipoId, l] of linhas) {
      l.aplicarPrevia(previa.tipos.get(tipoId) ?? null, previa.recusas.filter((r) => r.tipoId === tipoId));
    }
    for (const r of previa.recusas) {
      if (r.tipoId !== null && linhas.has(r.tipoId)) continue;
      avisoGeral.append(el("div", {}, situacaoComIcone("alerta", r.erro.message)));
    }
  };

  const montarTabela = (): void => {
    const snap = store.get();
    // o que cada peça mostra, pela regra única da Ficha em digitação (#91) —
    // relida a cada montagem: o que foi digitado já está na memória da página
    const valores = lote
      ? valoresIniciais(snap, lote.id, memoriaDaFicha.de(snap, lote.id)).linhas
      : (memoriaDaFicha.de(snap, NOVA_OS)?.linhas ?? null);

    linhas.clear();
    clear(areaTabela);
    const tipos = tiposDaFicha(snap);

    if (tipos.length === 0) {
      areaTabela.append(
        el("p", { class: "muted mini-vazio" }, "Nenhum tipo de peça ativo para este cliente."),
      );
      aplicarTravamento();
      recalcTotais();
      return;
    }

    montandoLinhas = true;
    for (const t of tipos) {
      const v = valores?.get(t.id);
      const linha: LinhaTipoPeca = criarLinhaTipoPeca({
        store,
        loteId: lote?.id ?? null,
        tipo: { id: t.id, nome: t.nome },
        ...(v
          ? {
              gravado: {
                recebida: v.recebida,
                devolvida: v.devolvida,
                conferida: v.conferida,
                conferidaMotivo: v.conferidaMotivo,
                retidaInformada: v.retidaInformada,
              },
            }
          : {}),
        retidasGravadas: v?.retidas ?? [],
        aoMudar: () => {
          // a linha avisa ainda durante a própria montagem (antes de existir
          // como `linha`): ali nada mudou — e a Prévia espera a última peça
          if (montandoLinhas) return;
          memoriaDaFicha.lembrarLinha(store.get(), chaveMemoria, t.id, comoDigitada(linha.colher()));
          atualizarPrevia();
          aplicarTravamento();
          recalcTotais();
        },
      });
      linhas.set(t.id, linha);
    }

    // peças divididas por categoria, cada uma recolhível com o resumo no
    // cabeçalho (variação C do protótipo, escolhida pelo dono)
    const catNome = new Map(snap.categorias.map((c) => [c.id, c.nome]));
    // escolha aberta/recolhida guardada só para OS já lançada (a Nova OS
    // ainda não tem id; a escolha dela vale enquanto a ficha estiver montada)
    let aberturas: Map<Id, boolean> | null = null;
    if (lote) {
      aberturas = aberturasPorFicha.get(lote.id) ?? new Map<Id, boolean>();
      aberturasPorFicha.set(lote.id, aberturas);
    }
    categorias = fichaCategorias({
      pecas: tipos.map((t) => ({ tipo: t, linha: linhas.get(t.id)! })),
      nomeDaCategoria: (id) => catNome.get(id) ?? "Sem Categoria",
      tiposDoCliente: new Set(tiposParaLancamento(snap, cliente.value).map((t) => t.id)),
      aberturas,
      cabecalhoColunas: () =>
        el(
          "div",
          { class: "ficha-linha-cab ficha-linha-titulo" },
          el("span", {}, "Peça"),
          el("span", {}, "Recebida"),
          el("span", {}, "Conferida"),
          el("span", {}, "Devolvida"),
          el("span", {}, "Retida"),
        ),
    });
    areaTabela.append(categorias.element);
    montandoLinhas = false;
    atualizarPrevia();
    aplicarTravamento();
    recalcTotais();
  };

  cliente.addEventListener("change", () => {
    sincOrigem(null);
    montarTabela();
    checarDiaParada();
  });
  // a data/hora de devolução decide de quais retidas a carona pode sair e
  // como fica o Backlog do hotel (ADR-0028) — recalcula as linhas
  for (const campoDevolucao of [dataDevolucao, horaDevolucao]) {
    campoDevolucao.addEventListener("input", lembrarDevolucao);
    campoDevolucao.addEventListener("change", () => {
      lembrarDevolucao();
      montarTabela();
    });
  }
  // o cabeçalho da Coleta também: uma remontagem não apaga o que foi digitado
  for (const campoColeta of [cliente, origem, dataColeta, horaColeta, numeroOs, sacos, urgente, relavagem, temPrazoManual, prazoData, prazoHora]) {
    campoColeta.addEventListener("change", lembrarCabecalho);
    campoColeta.addEventListener("input", lembrarCabecalho);
  }

  // ------------------------------------------------------------ ações ----

  const pill = el("span", { class: "pill-situacao" });
  const subtitulo = el("p", { class: "muted" });
  const notaBloqueio = el("span", { class: "ficha-nota-bloqueio" }, "Disponível depois de salvar a coleta");
  const notaStatus = el("div", { class: "aviso" });
  notaStatus.hidden = true;
  const btnFecharPainel = el(
    "button",
    { type: "button", class: "modal-x", "aria-label": "Fechar" },
    icon("close", 16, { peso: 2.2 }),
  );
  btnFecharPainel.addEventListener("click", () => callbacks.aoFechar());

  const erro = el("p", { class: "form-erro", role: "alert" });
  const btnLancar = botao("primario", {});
  // Só existe numa Nova OS (nunca ao editar) — o hotel costuma mandar várias
  // remessas seguidas; sem isso cada uma exige abrir modal, preencher, sair
  // e reabrir do zero. Fica em segundo plano porque "Lançar coleta" continua
  // sendo o caminho mais comum (uma OS só, revisar o que foi lançado).
  const btnLancarOutra = botao("secundario", {}, "Salvar e Lançar Outra");
  const btnFechar = botao("primario", {}, "Fechar OS");
  const btnReabrir = botao("perigo", {}, "Reabrir Fechamento");
  const btnPreencherComanda = botao(
    "secundario",
    {},
    "Preencher Conforme Comanda",
  );
  const btnLimparDados = botao("secundario", {}, "Limpar Dados");
  btnPreencherComanda.addEventListener("click", () => {
    if (btnPreencherComanda.disabled) return;
    for (const l of linhas.values()) l.aplicarComanda();
  });
  btnLimparDados.addEventListener("click", () => {
    if (btnLimparDados.disabled) return;
    for (const l of linhas.values()) l.limpar();
  });

  // "fechado" e "reaberta" (ADR-0024) são o mesmo caso pro toggle local de
  // edição: ambos já têm Fechamento gravado, então "Reabrir fechamento"
  // destrava do mesmo jeito nos dois — a diferença entre eles é só o motivo
  // de estarem bloqueados (nunca foi faturada vs. a Fatura foi reaberta).
  const jaFechou = (s: SituacaoFicha): boolean => s === "fechado" || s === "reaberta";

  const situacaoEfetiva = (): SituacaoFicha =>
    jaFechou(situacaoReal) && fechamentoDestravado ? "lancado" : situacaoReal;

  const aplicarTravamento = (): void => {
    const efetiva = situacaoEfetiva();
    const bloqueado =
      somenteLeitura || efetiva === "faturado" || efetiva === "cancelado" || jaFechou(efetiva);
    const semLote = efetiva === "novo";

    // O selo mostra sempre a situação GRAVADA, a mesma da lista de OS.
    // "Reabrir Fechamento" só destrava a edição na tela — a OS continua
    // fechada (e faturável) até o próximo "Fechar OS"; mostrar "Lançado" aqui
    // dizia uma situação que a OS não tem. O modo de edição vai no aviso.
    const destravado = efetiva === "lancado" && jaFechou(situacaoReal);
    pill.className = `pill-situacao pill-situacao-${situacaoReal}`;
    pill.textContent =
      situacaoReal === "novo" ? ROTULO_NOVO : rotuloSituacao(situacaoReal as SituacaoLote);
    subtitulo.textContent = lote
      ? `${lote.codigo} · coleta ${formatData(lote.coletaData)} ${lote.coletaHora}`
      : `Cliente sugerido: ${nomeCliente(cliente.value)}`;

    for (const c of [
      cliente, origem, dataColeta, horaColeta, numeroOs, sacos,
      urgente, relavagem, temPrazoManual, prazoData, prazoHora,
    ]) {
      c.disabled = bloqueado;
    }
    dataDevolucao.disabled = bloqueado || semLote;
    horaDevolucao.disabled = bloqueado || semLote;
    notaBloqueio.hidden = !semLote;
    fechamentoConteudo.hidden = semLote;
    btnPreencherComanda.disabled = bloqueado || semLote;
    btnLimparDados.disabled = bloqueado || semLote;

    for (const linha of linhas.values()) {
      linha.travar({ recebidaTravada: bloqueado, fechamentoTravado: bloqueado || semLote });
    }

    btnLancar.hidden = bloqueado;
    btnLancar.textContent = semLote ? "Lançar Coleta" : "Salvar";
    btnLancarOutra.hidden = bloqueado || !semLote;
    btnFechar.hidden = bloqueado || semLote;
    btnFechar.disabled = [...linhas.values()].some((l) => l.pendente());
    avisoGeral.hidden = bloqueado || semLote || avisoGeral.childElementCount === 0;
    btnReabrir.hidden = somenteLeitura || !jaFechou(efetiva);

    const reabertaParaCorrecao = situacaoReal === "lancado" && lote != null && emCorrecao(lote);
    notaStatus.hidden =
      situacaoReal !== "faturado" &&
      situacaoReal !== "cancelado" &&
      efetiva !== "reaberta" &&
      !destravado &&
      !reabertaParaCorrecao;
    if (destravado) {
      notaStatus.className = "aviso aviso-atencao";
      notaStatus.textContent =
        'Fechamento destravado — "Salvar" reabre a OS (ela fica Lançada e fora do faturamento); "Fechar OS" regrava o fechamento.';
    } else if (reabertaParaCorrecao) {
      notaStatus.className = "aviso aviso-atencao";
      notaStatus.textContent =
        'OS reaberta para correção — fica Lançada e fora do faturamento até você clicar em "Fechar OS".';
    } else if (situacaoReal === "faturado") {
      notaStatus.className = "aviso aviso-info";
      notaStatus.textContent = "Faturada — para corrigir, reabra a fatura em Faturamento por Cliente.";
    } else if (situacaoReal === "cancelado") {
      notaStatus.className = "aviso aviso-erro";
      notaStatus.textContent = `Cancelada - ${lote?.motivoCancelamento ?? ""}`;
    } else if (efetiva === "reaberta") {
      // ADR-0024: a Fatura que continha esta OS foi reaberta — o Fechamento
      // continua gravado (só travado); "Reabrir fechamento" destrava pra
      // corrigir antes de faturar de novo.
      notaStatus.className = "aviso aviso-atencao";
      notaStatus.textContent =
        'A fatura desta OS foi reaberta — clique em "Reabrir Fechamento" pra corrigir, ou cancele a OS se ela não deveria ter sido faturada.';
    }
  };

  /** O Fechamento como está na tela agora (data, hora e linhas). */
  const colherFechamento = (): { data: string; hora?: string; linhas: LinhaDevolucaoComando[] } => {
    const linhasComando: LinhaDevolucaoComando[] = [];
    for (const l of linhas.values()) {
      const c = l.colher();
      if (c.devolvida === 0 && c.retidas.length === 0 && c.conferida == null) continue;
      linhasComando.push({
        tipoId: c.tipoId,
        devolvida: c.devolvida,
        retidas: c.retidas,
        ...(c.conferida != null ? { conferida: c.conferida } : {}),
        ...(c.conferida != null && c.conferidaMotivo ? { conferidaMotivo: c.conferidaMotivo } : {}),
      });
    }
    return {
      data: dataDevolucao.value,
      ...(horaDevolucao.value ? { hora: horaDevolucao.value } : {}),
      linhas: linhasComando,
    };
  };

  /** O comando "gravar a Ficha" (#91) com o que está na tela: o domínio
   * escolhe o caminho pela situação da OS e grava Coleta e Fechamento juntos. */
  const comandoGravar = (id: Id, intencao: IntencaoFicha) =>
    comandoGravarFicha(
      id,
      { dados: colherDadosLote(), devolucao: { data: dataDevolucao.value, hora: horaDevolucao.value }, linhas: colherLinhas() },
      intencao,
      // "Reabrir Fechamento" clicado numa OS fechada: o Salvar reabre de verdade
      fechamentoDestravado && jaFechou(situacaoReal),
    );

  const salvar = (lancarOutra: boolean): void => {
    erro.textContent = "";
    btnLancar.disabled = true;
    btnLancarOutra.disabled = true;
    if (!lote) {
      const dados = colherDadosLote();
      void criarAcaoComando(store, erro)(
        { agregado: "lote", cmd: { tipo: "lancar", dados, recebidas: colherRecebidas(linhas) } },
        "OS lançada.",
        () => {
          // lançada: o que estava digitado na Nova OS agora é a OS gravada
          memoriaDaFicha.esquecer(NOVA_OS);
          const novo = store.get().lotes.find((l) => l.os === dados.os);
          // a OS anterior da próxima Nova OS em sequência (#88)
          if (novo) salvarJSON(CHAVE_ULTIMA_LANCADA, novo.id);
          if (novo) callbacks.aoLancarNova(novo.id, { lancarOutra, clienteId: dados.clienteId });
        },
      ).finally(() => {
        btnLancar.disabled = false;
        btnLancarOutra.disabled = false;
      });
      return;
    }
    const reabrir = fechamentoDestravado && jaFechou(situacaoReal);
    void criarAcaoComando(store, erro)(
      comandoGravar(lote.id, "salvar"),
      reabrir ? "OS reaberta — fica Lançada até Fechar OS." : "OS salva.",
      () => {
        // gravado: o banco já tem o que estava na memória da página
        memoriaDaFicha.esquecer(lote.id);
        if (reabrir) lotesComFechamentoDestravado.delete(lote.id);
      },
    ).finally(() => {
      btnLancar.disabled = false;
      btnLancarOutra.disabled = false;
    });
  };
  btnLancar.addEventListener("click", () => salvar(false));
  btnLancarOutra.addEventListener("click", () => salvar(true));

  // "Fechar OS": a Ficha inteira num comando só — Coleta desatualizada e
  // Fechamento gravam juntos, e uma recusa não grava nada (#91)
  btnFechar.addEventListener("click", () => {
    if (!lote || btnFechar.disabled) return;
    erro.textContent = "";
    const comando = comandoGravar(lote.id, "fechar");
    // `lotesComFechamentoDestravado` sai ANTES do comando disparar, não num
    // callback de sucesso — é o Set que a ficha remontada lê (ver
    // `comMutacaoOtimista` em `_acao.ts`); volta se o comando falhar.
    let estavaDestravado = false;
    const acao = comMutacaoOtimista(
      criarAcaoComando(store, erro),
      () => {
        estavaDestravado = lotesComFechamentoDestravado.has(lote.id);
        lotesComFechamentoDestravado.delete(lote.id);
      },
      () => {
        if (estavaDestravado) lotesComFechamentoDestravado.add(lote.id);
      },
    );
    btnFechar.disabled = true;
    void acao(comando, "OS fechada.", () => {
      memoriaDaFicha.esquecer(lote.id);
      fechamentoDestravado = false;
    }).finally(() => {
      btnFechar.disabled = false;
    });
  });
  btnReabrir.addEventListener("click", () => {
    if (lote) lotesComFechamentoDestravado.add(lote.id);
    fechamentoDestravado = true;
    aplicarTravamento();
  });

  // Corpo do Fechamento: escondido (não só com campos desabilitados) até a
  // Coleta ser salva pela primeira vez — ver `aplicarTravamento`. Cabeçalho
  // + `notaBloqueio` continuam visíveis pra explicar por que a seção existe.
  const fechamentoConteudo = el(
    "div",
    {},
    el(
      "div",
      { class: "grid-2" },
      // data e hora da devolução ordenam o Backlog — a hora é obrigatória (ADR-0028)
      campoObrigatorio("Data", dataDevolucao),
      campoObrigatorio("Hora", horaDevolucao),
    ),
    veredito,
    el("div", { class: "ficha-utilitarios" }, btnPreencherComanda, btnLimparDados),
    areaTabela,
    totais,
    avisoGeral,
  );

  montarTabela();


  return el(
    "div",
    { class: "ficha-painel" },
    el(
      "div",
      { class: "ficha-cab" },
      el("div", {}, el("h2", {}, lote ? `OS ${lote.os}` : "Nova OS"), subtitulo),
      el("div", { class: "ficha-cab-acoes" }, pill, btnFecharPainel),
    ),
    el(
      "div",
      { class: "ficha-secao" },
      el("h3", {}, "Coleta"),
      escolhaSequencia,
      avisoSequencia,
      campo("Cliente", cliente),
      campo("Origem da Coleta", origem),
      el("div", { class: "grid-2" },
        campo("Data da Coleta", dataColeta),
        campo("Hora", horaColeta)
      ),
      avisoDia,
      el("div", { class: "grid-2" },
        campo("OS (Comanda)", numeroOs),
        campo("Sacos", sacos)
      ),
      el(
        "div",
        { class: "campo" },
        el("label", { class: "check" }, urgente, " OS urgente"),
        el("label", { class: "check" }, relavagem, " OS de relavagem"),
        el("label", { class: "check" }, temPrazoManual, " Prazo de entrega diferente para esta OS"),
        prazoManualCampos,
      ),
    ),
    el(
      "div",
      { class: "ficha-secao" },
      el("div", { class: "ficha-secao-cab" }, el("h3", {}, "Fechamento"), notaBloqueio),
      fechamentoConteudo,
    ),
    notaStatus,
    erro,
    el("div", { class: "ficha-acoes" }, btnReabrir, btnLancarOutra, btnLancar, btnFechar),
  );
}
